import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthContext } from '../../auth/context';
import type { IEntity } from '../../auth/types';
import { GetTokenUsage } from '../api';
import type { TokenUsageBreakdown } from '../api';
import { useUpgradeState } from './useUpgradeState';
import { FEATURES, MAX_QUESTIONS_PER_GENERATION } from '../constants';
import { useUpgradeDialogStore } from 'store/upgradeDialog';

// Re-export FEATURES constant for convenience — frontend code imports from here
export { FEATURES } from '../constants';

export function useFeature(featureKey: string): boolean {
  const { state } = useAuthContext();
  return state.entitlements?.features[featureKey]?.enabled ?? false;
}

/** null → unlimited, 0 → not enabled, number → cap. */
export function useFeatureLimit(featureKey: string): number | null {
  const { state } = useAuthContext();
  const feature = state.entitlements?.features[featureKey];
  if (!feature?.enabled) return 0;
  return feature.limit ?? null;
}

/** Falls back to the absolute ceiling when unlimited or not yet seeded. UX affordance — the backend gates. */
export function useMaxQuestionsPerGeneration(): number {
  const { state } = useAuthContext();
  const feature = state.entitlements?.features[FEATURES.AI_QUESTIONS_PER_GENERATION];
  if (!feature || feature.limit == null) return MAX_QUESTIONS_PER_GENERATION;
  return Math.min(MAX_QUESTIONS_PER_GENERATION, feature.limit);
}

/** Same dialog the 403 interceptor shows, so the copy and the billing-aware CTA stay in one place. */
export function useOpenUpgradeForLimit(): (
  featureKey: string,
  limit: number,
  requested?: number,
) => void {
  const openDialog = useUpgradeDialogStore((s) => s.open);
  const { state } = useAuthContext();
  const planKey = state.entitlements?.planKey;
  return useCallback(
    (featureKey, limit, requested) =>
      openDialog({
        code: 'USAGE_LIMIT_EXCEEDED',
        featureKey,
        upgrade: { currentPlan: planKey, limit, used: requested ?? limit + 1 },
      }),
    [openDialog, planKey],
  );
}

/** Upsells a gated action on click, so a Trial/Starter user sees the prompt instead of a failed request. */
export function useOpenUpgradeForFeature(): (featureKey: string) => void {
  const openDialog = useUpgradeDialogStore((s) => s.open);
  const { state } = useAuthContext();
  const planKey = state.entitlements?.planKey;
  return useCallback(
    (featureKey) =>
      openDialog({
        code: 'FEATURE_NOT_ENTITLED',
        featureKey,
        upgrade: { currentPlan: planKey },
      }),
    [openDialog, planKey],
  );
}

/** Current period usage and limit for a metered feature. */
export function useFeatureUsage(featureKey: string): { used: number; limit: number | null } {
  const { state } = useAuthContext();
  const feature = state.entitlements?.features[featureKey];
  return {
    used: feature?.used ?? 0,
    limit: feature?.limit ?? null,
  };
}

/** Use when you need to inspect several features at once. */
export function useEntitlements(): IEntity.OrgEntitlements | null {
  const { state } = useAuthContext();
  return state.entitlements;
}

export function usePlan(): { key: string; name: string } {
  const { state } = useAuthContext();
  return {
    key: state.entitlements?.planKey ?? 'FREE',
    name: state.entitlements?.planName ?? 'Free',
  };
}

/** Delegates to `useUpgradeState` so the banner and the header badge stay in lockstep. */
export function useSubscriptionAlert(): { hasAlert: boolean; status: string | null } {
  const { reason, bannerSeverity } = useUpgradeState();
  return {
    hasAlert: bannerSeverity != null,
    status: reason,
  };
}

/** Stale after 60 seconds — suitable for billing-page display. */
export function useTokenUsage(periodStart?: string): {
  data: TokenUsageBreakdown | undefined;
  isLoading: boolean;
} {
  const { state } = useAuthContext();
  const orgId = state.organization?.id;

  return useQuery<TokenUsageBreakdown>({
    queryKey: ['token-usage', orgId, periodStart],
    queryFn: async () => {
      const { data } = await GetTokenUsage(periodStart);
      return data;
    },
    staleTime: 60_000,
    enabled: !!orgId,
  });
}
